import api from 'api/client';
import { ENDPOINTS } from 'api/endpoints';
import { AddToCartRequest, Cart, CartItem, UpdateCartItemRequest } from 'types/cart';
import catalogConfigService from 'services/catalogConfigService';

type StoreCartItemApi = {
  id?: number;
  Id?: number;
  sku?: string;
  Sku?: string;
  slug?: string;
  Slug?: string;
  name?: string;
  Name?: string;
  quantity?: number;
  Quantity?: number;
  unitPrice?: number;
  UnitPrice?: number;
  imageUrl?: string;
  ImageUrl?: string;
};

type StoreCartApi = {
  cartId?: number;
  items?: StoreCartItemApi[];
  Items?: StoreCartItemApi[];
  subtotal?: number;
  Subtotal?: number;
  itemCount?: number;
  currency?: string;
  Currency?: string;
};

const mapItem = (item: StoreCartItemApi): CartItem => {
  const sku = String(item.sku || item.Sku || '').trim();
  return {
    lineId: item.id ?? item.Id,
    productId: sku,
    slug: String(item.slug || item.Slug || sku).trim(),
    name: String(item.name || item.Name || sku).trim(),
    quantity: Number(item.quantity ?? item.Quantity ?? 0),
    unitPrice: Number(item.unitPrice ?? item.UnitPrice ?? 0),
    imageUrl: item.imageUrl || item.ImageUrl,
  };
};

const mapCart = (data: StoreCartApi): Cart => {
  const items = (data.items || data.Items || []).map(mapItem);
  const subtotal = Number(data.subtotal ?? data.Subtotal ?? items.reduce((s, i) => s + i.unitPrice * i.quantity, 0));
  return {
    items,
    subtotal,
    discount: 0,
    total: subtotal,
    currency: data.currency || data.Currency || catalogConfigService.getDefaultCurrency(),
  };
};

const cartService = {
  getCart: async (): Promise<Cart> => {
    const response = await api.get<StoreCartApi>(ENDPOINTS.CART);
    return mapCart(response.data);
  },

  addItem: async (data: AddToCartRequest): Promise<Cart> => {
    const response = await api.post<StoreCartApi>(ENDPOINTS.CART_ITEMS, {
      sku: data.productId,
      quantity: data.quantity,
    });
    return mapCart(response.data);
  },

  updateItem: async (data: UpdateCartItemRequest & { lineId?: number }): Promise<Cart> => {
    let lineId = data.lineId;
    if (lineId == null) {
      const current = await cartService.getCart();
      lineId = current.items.find((i) => i.productId === data.productId)?.lineId;
    }
    if (lineId == null) {
      throw new Error('Ítem de carrito no encontrado');
    }

    if (data.quantity <= 0) {
      const response = await api.delete<StoreCartApi>(ENDPOINTS.CART_ITEM(lineId));
      return mapCart(response.data || { items: [] });
    }

    const response = await api.put<StoreCartApi>(ENDPOINTS.CART_ITEM(lineId), {
      quantity: data.quantity,
    });
    return mapCart(response.data);
  },

  clearCart: async (): Promise<void> => {
    await api.delete(ENDPOINTS.CART);
  },
};

export default cartService;
